import { useApolloClient } from "@apollo/client";
import { MenuItemWithIcon, ModalOpenButton } from "@msys/ui";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { Alert } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { namedOperations } from "../../../../clients/graphqlTypes.js";
import { ConfirmModal } from "../../../commons/modals/ConfirmModal.js";
import { useDeletePimSupplierImportationsMutation } from "./DeletePimSupplierImportationsButton.generated.js";

/**
 * "Delete catalogue" entry for the imported-catalogues header overflow menu.
 * Deletes the whole supplier's catalogue (the existing mutation) behind an
 * archive-warning confirmation.
 */
export const DeletePimSupplierImportationsMenuItem = ({
  supplierId,
}: {
  supplierId: string;
}) => {
  const { t } = useTranslate(["PimImportations", "Global"]);

  const client = useApolloClient();

  const [deletePimSupplierImportations] =
    useDeletePimSupplierImportationsMutation({
      client,
      refetchQueries: [namedOperations.Query.SupplierPimImportations],
    });

  return (
    <ModalOpenButton
      Modal={ConfirmModal}
      modalProps={{
        title: t("Delete catalogue", { ns: "PimImportations" }),
        content: () => (
          <Alert severity="warning">
            {t(
              "You are about to delete this supplier's catalogue. This action cannot be undone. Products from this supplier will not be available for new quotes. Products used in quotes will be marked as archived.",
              { ns: "PimImportations" }
            )}
          </Alert>
        ),
        confirmButtonLabel: t("Delete catalogue", { ns: "PimImportations" }),
        confirmButtonProps: { color: "error" },
        handleConfirm: async () => {
          await deletePimSupplierImportations({
            variables: {
              input: {
                supplierId,
              },
            },
          });
        },
      }}
    >
      <MenuItemWithIcon icon={<DeleteIcon />}>
        {t("Delete catalogue", { ns: "PimImportations" })}
      </MenuItemWithIcon>
    </ModalOpenButton>
  );
};
